/**
 * Utility to apply featured tour order to any list of tours.
 * Reads both `sortOrder` (camelCase, in-memory normalized) and
 * `sort_order` (snake_case, from Postgres / static JSON) so that
 * the admin's drag-to-reorder is ALWAYS respected on the client.
 */

import { Tour } from '../types';

/** Safely resolves sortOrder from either field name */
function getSortOrder(tour: any): number {
  const val = (tour as any).sortOrder ?? (tour as any).sort_order;
  if (val === null || val === undefined) return 9999;
  const n = Number(val);
  return Number.isFinite(n) ? n : 9999;
}

/**
 * Sort tours by admin-set featured order (ascending sortOrder).
 * Ties fall back to newest-first by createdAt.
 */
export function applyFeaturedOrder(tours: Tour[]): Tour[] {
  if (!tours || tours.length === 0) return tours;

  return [...tours].sort((a, b) => {
    const orderDiff = getSortOrder(a) - getSortOrder(b);
    if (orderDiff !== 0) return orderDiff;
    // Tie-break: newest first
    const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bDate - aDate;
  });
}

/**
 * Apply featured order on a category-scoped list.
 * sortOrder is global (admin sets it once, respected in all views).
 */
export function applyFeaturedOrderWithCategory(
  tours: Tour[],
  _categoryId?: string
): Tour[] {
  return applyFeaturedOrder(tours);
}
